//#region IMPORT
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import dayjs from 'dayjs';
import { apiRequest } from '../../../Services/Common/ApiRequest';
//#endregion

/**
 * 위험성평가 - 위험성평가 결과서 (RIS_BASIC_02) 전용 로직
 * other 케이스에서 사용
 */

// 이미지 URL을 Base64로 안전하게 변환하는 함수
const getBase64ImageSafe = async (url) => {
  try {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      return null;
    }

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
    });

    if (!response.ok) {
      console.warn('파일 없음 또는 접근 불가:', url, response.status);
      return null;
    }

    // Content-Type 확인
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.startsWith('image/')) {
      console.warn('이미지 파일이 아님:', url, contentType);
      return null;
    }

    const blob = await response.blob();

    // Blob 크기 확인 (너무 큰 파일 방지)
    if (blob.size === 0) {
      console.warn('빈 파일:', url);
      return null;
    }

    if (blob.size > 10 * 1024 * 1024) {
      // 10MB 이상이면 스킵
      console.warn('파일 크기가 너무 큼:', url, blob.size);
      return null;
    }

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        try {
          const result = reader.result;
          if (
            result &&
            typeof result === 'string' &&
            result.startsWith('data:image/')
          ) {
            resolve(result);
          } else {
            console.warn('유효하지 않은 이미지 데이터:', url);
            resolve(null);
          }
        } catch (e) {
          console.warn('이미지 데이터 처리 오류:', url, e);
          resolve(null);
        }
      };
      reader.onerror = () => {
        console.warn('FileReader 오류:', url);
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('이미지 fetch 오류:', url, e);
    return null; // 에러 나면 null 반환
  }
};

// 위험성평가 결과서 인쇄 함수 (PrintLogic1)
export const PrintLogic1 = async (_value1, dispatch, setLoading, searchRef) => {
  setLoading(true);

  try {
    // 선택된 항목: 단일 객체 또는 배열
    const selectedItems = Array.isArray(_value1)
      ? _value1
      : _value1
        ? [_value1]
        : [];

    const custCd = searchRef?.current?.custCd;
    if (!custCd) {
      dispatch(
        showSnackbar({
          message: '사업장코드가 없습니다.',
          type: 'warning',
        })
      );
      return null;
    }

    let allPrintTableData = [];

    for (const selectedItem of selectedItems) {
      const rsCd = selectedItem.rsCd || selectedItem.RS_CD;
      if (!rsCd) {
        dispatch(
          showSnackbar({
            message: '위험성평가 코드가 없습니다.',
            type: 'warning',
          })
        );
        continue;
      }

      try {
        const result = await apiRequest(
          'post',
          Global.urls.DocsManagement.getRisBasic02R1,
          {
            RsCd: Commons.EncryptAES(rsCd),
          }
        );

        const data = result?.data;
        const rows = Array.isArray(data) ? data : data ? [data] : [];

        if (rows.length === 0 || rows[0]?.type === 'Error') {
          if (rows[0]?.type === 'Error') {
            dispatch(showSnackbar({ message: rows[0].message, type: 'error' }));
          }
          continue;
        }

        const baseNo = allPrintTableData.length;
        const printTableData = await Promise.all(
          rows.map(async (row, index) => {
            let base64Before = null;
            if (row.rsrBeforeImageSaveName && row.rsCd && row.rsrCd) {
              try {
                const filePath1 = row.rsrBeforeImageFilePath;
                const url1 = `${Global.file.useUrl}files/${filePath1}`;
                const base64BeforeFull = await getBase64ImageSafe(url1);
                if (
                  base64BeforeFull &&
                  base64BeforeFull.startsWith('data:image/')
                ) {
                  base64Before = base64BeforeFull.replace(
                    /^data:image\/[a-z]+;base64,/,
                    ''
                  );
                  if (!base64Before || base64Before.trim() === '') {
                    base64Before = null;
                  }
                }
              } catch (e) {
                console.warn('Before 이미지 처리 오류:', e);
                base64Before = null;
              }
            }

            let base64After = null;
            if (row.rsrAfterImageSaveName && row.rsCd && row.rsrCd) {
              try {
                const filePath2 = row.rsrAfterImageFilePath;
                const url2 = `${Global.file.useUrl}files/${filePath2}`;
                const base64AfterFull = await getBase64ImageSafe(url2);
                if (
                  base64AfterFull &&
                  base64AfterFull.startsWith('data:image/')
                ) {
                  base64After = base64AfterFull.replace(
                    /^data:image\/[a-z]+;base64,/,
                    ''
                  );
                  if (!base64After || base64After.trim() === '') {
                    base64After = null;
                  }
                }
              } catch (e) {
                console.warn('After 이미지 처리 오류:', e);
                base64After = null;
              }
            }

            return {
              NO: baseNo + index + 1,
              DEP_NM_LAST: row.depNmLast || '',
              RSR_PROBLEM: row.rsrProblem || '',
              RSR_GAESUN: row.rsrGaesun || '',
              REDUCTION_MEASURES: row.reductionMeasures || '',
              RSR_COM_DATE: row.gaesunComDate
                ? dayjs(row.gaesunComDate).format('YYYY-MM-DD')
                : '',
              RSR_ID: row.riskId,
              RSR_BEFORE_IMAGE: base64Before || '',
              RSR_AFTER_IMAGE: base64After || '',
              REMARK: row.remark || '',
            };
          })
        );

        allPrintTableData = [...allPrintTableData, ...printTableData];
      } catch (e) {
        console.error('결과서 데이터 조회 오류:', e);
        dispatch(
          showSnackbar({
            message: `위험성평가 코드 ${rsCd}의 데이터 조회 중 오류가 발생했습니다.`,
            type: 'error',
          })
        );
        continue;
      }
    }

    if (allPrintTableData.length === 0) {
      dispatch(
        showSnackbar({
          message: '조회된 데이터가 없습니다.',
          type: 'warning',
        })
      );
      return null;
    }

    const headerData = {
      CREATE_DATE: dayjs().format('YYYY-MM-DD'),
      CUST_CD: custCd,
    };

    const printData = {
      FileName: 'RIS_BASIC_02_2',
      HeaderData: JSON.stringify(headerData),
      TableData: JSON.stringify(allPrintTableData),
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    return response.data;
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};

// 근로자 참여 체크리스트 인쇄 함수 (PrintLogic2)
export const PrintLogic2 = async (
  documentData,
  dispatch,
  setLoading,
  rowData1,
  searchRef
) => {
  setLoading(true);
  try {
    // selectedRowData: documentData 단일 또는 rowData1 배열
    const selectedRowData = documentData
      ? [documentData]
      : rowData1 && rowData1.length > 0
        ? rowData1
        : [];

    if (selectedRowData.length === 0) {
      dispatch(
        showSnackbar({
          message: '위험성평가표 항목을 선택하세요.',
          type: 'warning',
        })
      );
      return null;
    }

    // 선택된 각 항목에 대해 체크리스트 데이터 조회 및 인쇄
    for (const selectedItem of selectedRowData) {
      const rsCd = selectedItem.rsCd || selectedItem.RS_CD;
      if (!rsCd) {
        dispatch(
          showSnackbar({
            message: '위험성평가 코드가 없습니다.',
            type: 'warning',
          })
        );
        continue;
      }

      // 체크리스트 데이터 조회
      let checklistTableData = [];
      let checklistData = null; // API 조회 결과 (관리자 checklistData 상태에 대응)

      try {
        const result = await apiRequest(
          'post',
          Global.urls.DocsManagement.getRisBasic02R2,
          {
            RsCd: Commons.EncryptAES(rsCd),
          }
        );

        const data = result?.data;
        checklistData = Array.isArray(data) ? data : data ? [data] : [];

        // 체크리스트 기본 항목 정의 (관리자와 동일 - 루프 내부)
        const defaultChecklistItems = [
          {
            id: 1,
            question:
              '근로자에게 위험성평가의 필요성, 방법 등 필요한 교육을 하였나요?',
          },
          {
            id: 2,
            question:
              '위험요인 발굴을 위한 순회점검을 할 때 해당 작업 근로자를 포함하고 있나요?',
          },
          {
            id: 3,
            question:
              '사업장 내 유해위험기계기구 및 물질, 위험장소 등을 대부분의 근로자가 알고 있나요?',
          },
          {
            id: 4,
            question:
              '산업재해 및 아차사고 발생 사례를 안내하여 대부분의 근로자가 알고 있나요?',
          },
          {
            id: 5,
            question:
              '산업안전보건위원회, 도급인수급인 합동점검 등을 통해 구성원의 의견을 적극적으로 수렴하고 있나요?',
          },
          {
            id: 6,
            question:
              '작업 전 안전점검회의(TBM), 안전제안활동, 안전보건신고함 등 구성원의 의견을 수렴하는 절차를 운영하나요?',
          },
          {
            id: 7,
            question:
              '위험요인 신고, 제도개선, 제안하는 근로자에게 인센티브를 부여하고 있나요?',
          },
          {
            id: 8,
            question:
              '위험요인 신고제안자에게 불이익 없도록 하여 자유롭게 의견을 제시할 수 있도록 하나요?',
          },
          {
            id: 9,
            question:
              '신고 및 제안에 대한 조치결과를 정기적으로 공개하고 있나요?',
          },
        ];

        // 조회된 데이터를 체크리스트 항목에 매핑 (관리자: checklistData.items.find / 모바일: API 응답으로 매칭)
        checklistTableData = defaultChecklistItems.map((item, index) => {
          const found = checklistData.find(
            (d) =>
              d &&
              (d.CHECK_ITEM === item.question || d.checkItem === item.question)
          );
          let checkValue = null;
          if (found) {
            // DB CHECK_YN: 0(예), 1(아니오), 2(해당없음) / 관리자 UI는 checked 'yes'|'no'|'na'
            const checkYn = found.CHECK_YN ?? found.checkYn ?? found.checked;
            if (checkYn === '0' || checkYn === 0 || checkYn === 'yes')
              checkValue = 0;
            else if (checkYn === '1' || checkYn === 1 || checkYn === 'no')
              checkValue = 1;
            else if (checkYn === '2' || checkYn === 2 || checkYn === 'na')
              checkValue = 2;
          }
          return {
            NO: index + 1,
            CHECK_ITEM: item.question,
            CHECK_YN: checkValue,
          };
        });

        // 저장된 체크리스트 값이 있는지 확인 (체크된 항목이 하나라도 있는지)
        const hasCheckedItems = checklistTableData.some(
          (item) => item.CHECK_YN !== null && item.CHECK_YN !== undefined
        );

        if (!hasCheckedItems) {
          dispatch(
            showSnackbar({
              message: '인쇄할 체크리스트가 없습니다.',
              type: 'warning',
            })
          );
          continue;
        }
      } catch (e) {
        console.error('체크리스트 데이터 조회 오류:', e);
        dispatch(
          showSnackbar({
            message: '체크리스트 데이터 조회 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        continue;
      }

      // HeaderData 생성 - 조회한 데이터에서 CREATE_DATE 가져오기 (첫 번째 데이터의 CREATE_DATE 사용)
      let createDate = '';
      if (
        checklistData &&
        checklistData.length > 0 &&
        (checklistData[0].CREATE_DATE || checklistData[0].createDate)
      ) {
        createDate = dayjs(
          checklistData[0].CREATE_DATE || checklistData[0].createDate
        ).format('YYYY-MM-DD');
      } else if (selectedItem.rsDate || selectedItem.RS_DATE) {
        createDate = dayjs(
          selectedItem.rsDate || selectedItem.RS_DATE
        ).format('YYYY-MM-DD');
      } else {
        createDate = dayjs().format('YYYY-MM-DD');
      }

      const headerData = {
        CREATE_DATE: createDate,
        RS_CD: rsCd,
        RS_NM: selectedItem.rsNm || selectedItem.RS_NM || '',
        CUST_CD: searchRef?.current?.custCd || '',
      };

      const printData = {
        FileName: 'RIS_BASIC_02_3',
        HeaderData: JSON.stringify(headerData),
        TableData: JSON.stringify(checklistTableData),
      };

      const response = await Commons.Axios(
        'report',
        Global.base.useUrl + Global.urls.Reports.reports,
        printData
      );

      return response.data;
    }

    return null;
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};

// 근로자참여 서명지 인쇄 함수 (PrintLogic3) - topPrintButtons printType: '4'
export const PrintLogic3 = async (_value1, dispatch, setLoading, searchRef) => {
  setLoading(true);

  try {
    const headerData = {
      CUST_CD: searchRef?.current?.custCd || '',
    };

    const printData = {
      FileName: 'RIS_BASIC_02_4',
      HeaderData: JSON.stringify(headerData),
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    return response.data; // Blob 반환
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};

// 위험성평가 실시규정 인쇄 함수 (PrintLogic4) - topPrintButtons printType: '3'
export const PrintLogic4 = async (_value1, dispatch, setLoading, searchRef) => {
  setLoading(true);

  let headerData = {
    custNm:
      searchRef?.current?.custList?.filter(
        (e) => e.value === searchRef?.current?.custCd
      )[0]?.custNm || '',
  };

  try {
    const printData = {
      FileName: 'BOCB0000',
      headerData: headerData,
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    return response.data; // Blob 반환
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};
